/**
 * Scheduled Follow-Up Tagging System
 *
 * Sibling to the follow-up (`./follow-up-tagging`) and snooze
 * (`./snooze-tagging`) systems. Lets a salesperson set a deal to be followed up
 * on a SPECIFIC future week — a visible, intentional commitment (distinct from
 * snooze, which is passive suppression of one or more weeks).
 *
 * Tag Format: "followup-on-{YYYY-W##}" (e.g., "followup-on-2026-W30").
 *
 * Semantics (agreed with sales):
 *   - A deal has AT MOST ONE scheduled follow-up week. Setting a new one
 *     replaces any existing scheduled tag.
 *   - "Suppress until target week": while the scheduled week is in the FUTURE,
 *     the deal is hidden from the "Not Followed Up This Week" filter and the
 *     Slack reminder (`hasFutureScheduledFollowup`). On the target week it
 *     reappears as due; a missed (past) target week no longer suppresses.
 *
 * Stored in the same `deals.tags` text[] as the follow-up/snooze tags; the
 * `followup-on-` prefix keeps the namespace independent.
 */

import { getISOWeekString, compareIsoWeeks } from "./iso-week";

/** Prefix that marks a tag as a scheduled-follow-up tag. */
export const SCHEDULED_FOLLOWUP_TAG_PREFIX = "followup-on-";

/** Build the scheduled-follow-up tag for the week containing `date`. */
export function getScheduledFollowupTag(date: Date): string {
  return `${SCHEDULED_FOLLOWUP_TAG_PREFIX}${getISOWeekString(date)}`;
}

/** Extract the scheduled-follow-up week strings from a tags array, sorted. */
export function getScheduledFollowupWeeks(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  return tags
    .filter((tag) => tag.startsWith(SCHEDULED_FOLLOWUP_TAG_PREFIX))
    .map((tag) => tag.slice(SCHEDULED_FOLLOWUP_TAG_PREFIX.length))
    .sort();
}

/**
 * The deal's single scheduled-follow-up week, or null. If multiple tags somehow
 * exist (legacy), returns the EARLIEST — the soonest commitment wins.
 */
export function getScheduledFollowupWeek(tags?: string[]): string | null {
  const weeks = getScheduledFollowupWeeks(tags);
  return weeks.length > 0 ? weeks[0] : null;
}

/**
 * Set the scheduled follow-up to the week containing `date`, replacing any
 * existing scheduled-follow-up tag (single target). Returns updated tags.
 */
export function setScheduledFollowupWeek(tags: string[] | undefined, date: Date): string[] {
  const withoutScheduled = clearScheduledFollowup(tags);
  return [...withoutScheduled, getScheduledFollowupTag(date)];
}

/** Remove all scheduled-follow-up tags. Returns updated tags. */
export function clearScheduledFollowup(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  return tags.filter((tag) => !tag.startsWith(SCHEDULED_FOLLOWUP_TAG_PREFIX));
}

/**
 * Does this deal have a scheduled follow-up for a week strictly AFTER the
 * current week? This is the suppression check used by the reminder query and
 * the "Not Followed Up This Week" filter — a future commitment hides the deal
 * until its week arrives.
 */
export function hasFutureScheduledFollowup(tags?: string[]): boolean {
  const week = getScheduledFollowupWeek(tags);
  if (!week) return false;
  return compareIsoWeeks(week, getISOWeekString()) > 0;
}

/** Is the scheduled follow-up exactly the current week (i.e. due now)? */
export function isScheduledForThisWeek(tags?: string[]): boolean {
  const week = getScheduledFollowupWeek(tags);
  if (!week) return false;
  return compareIsoWeeks(week, getISOWeekString()) === 0;
}

/** Any scheduled follow-up at or after the current week (active commitment). */
export function hasActiveScheduledFollowup(tags?: string[]): boolean {
  const week = getScheduledFollowupWeek(tags);
  if (!week) return false;
  return compareIsoWeeks(week, getISOWeekString()) >= 0;
}
